// src/features/records/components/ShareDialog/components/ManageAccessTable.js

import React, { useState, useEffect, useRef } from 'react';
import { Lock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RoleSelect from './RoleSelect';
import { getUserProfileReadOnly } from '../../../../../services/userService';
import { getGroupById } from '../../../../groups';
import accessControlService from '../../../services/accessControlService';
import { Avatar, AVATAR_VARIANTS } from '../../../../../components/avatars';
import mapRecordsErrorToKey from '../../../utils/recordsErrorMapper';
import styles from './ManageAccessTable.module.css';

const ManageAccessTable = ({ 
  record,
  grants = [],
  currentUserId,
  isAdmin = false,
  onRoleChange,
  onRemove
}) => {
  const { t } = useTranslation();
  const [grantDetails, setGrantDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const requestVersionRef = useRef(0);

  // Fetch user/group details for each grant
  useEffect(() => {
    let isActive = true;
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;

    const fetchGrantDetails = async () => {
      setLoading(true);
      setLoadError(null);
      
      try {
        const ownerId = record?.access?.owner;
        const unknownUserLabel = t('records.share.manage.unknown_user', 'Unknown user');
        const unknownGroupLabel = t('records.share.manage.unknown_group', 'Unknown group');

        const normalizedGrants = grants
          .map((grant) => ({
            grant,
            principal: accessControlService.parsePrincipalKey(grant.id),
          }))
          .filter(({ principal }) => {
            if (!principal?.id || !principal?.type) {
              return false;
            }
            if (principal.type === 'user' && principal.id === ownerId) {
              return false;
            }
            if (principal.id.startsWith('friends:') || principal.id === '*') {
              return false;
            }
            return true;
          });

        const userIds = new Set();
        const groupIds = new Set();
        if (ownerId) {
          userIds.add(ownerId);
        }

        normalizedGrants.forEach(({ principal }) => {
          if (principal.type === 'user') {
            userIds.add(principal.id);
          } else if (principal.type === 'group') {
            groupIds.add(principal.id);
          }
        });

        const [userEntries, groupEntries] = await Promise.all([
          Promise.all(
            Array.from(userIds).map(async (userId) => {
              try {
                const profile = await getUserProfileReadOnly(userId);
                return [userId, profile];
              } catch (error) {
                console.error('Error fetching user profile:', error);
                return [userId, null];
              }
            })
          ),
          Promise.all(
            Array.from(groupIds).map(async (groupId) => {
              try {
                const group = await getGroupById(groupId);
                return [groupId, group];
              } catch (error) {
                console.error('Error fetching group:', error);
                return [groupId, null];
              }
            })
          ),
        ]);

        if (!isActive || requestVersionRef.current !== requestVersion) {
          return;
        }

        const userMap = new Map(userEntries);
        const groupMap = new Map(groupEntries);
        const details = [];

        if (ownerId) {
          const ownerProfile = userMap.get(ownerId);
          details.push({
            id: `user:${ownerId}`,
            type: 'user',
            principalId: ownerId,
            name:
              ownerProfile?.displayName ||
              ownerProfile?.username ||
              unknownUserLabel,
            email: ownerProfile?.email,
            avatar: ownerProfile?.profilePictureUrl,
            role: 'admin',
            isOwner: true,
            canRemove: false,
            sources: ['owner']
          });
        }

        normalizedGrants.forEach(({ grant, principal }) => {
          let name;
          let avatar;
          let memberCount;
          let email;

          if (principal.type === 'user') {
            const profile = userMap.get(principal.id);
            name = profile?.displayName || profile?.username || unknownUserLabel;
            avatar = profile?.profilePictureUrl;
            email = profile?.email;
          } else if (principal.type === 'group') {
            const group = groupMap.get(principal.id);
            name = group?.name || unknownGroupLabel;
            avatar = group?.icon;
            memberCount = group?.memberCount;
          } else {
            return;
          }

          details.push({
            id: grant.id,
            type: principal.type,
            principalId: principal.id,
            name,
            avatar,
            email,
            role: grant.role,
            memberCount,
            isOwner: false,
            canRemove: isAdmin && principal.type !== 'public',
            sources: Array.isArray(grant.sources) ? grant.sources : []
          });
        });

        setGrantDetails(details);
      } catch (error) {
        if (!isActive || requestVersionRef.current !== requestVersion) {
          return;
        }
        console.error('Error fetching grant details:', error);
        setLoadError(
          mapRecordsErrorToKey(error, {
            fallbackKey: 'records.share.errors.load_permissions_failed'
          })
        );
      } finally {
        if (!isActive || requestVersionRef.current !== requestVersion) {
          return;
        }
        setLoading(false);
      }
    };

    fetchGrantDetails();

    return () => {
      isActive = false;
    };
  }, [grants, record?.access?.owner, currentUserId, isAdmin, t]);

  const handleRoleChange = (grant, newRole) => {
    if (!isAdmin || grant.isOwner) return;
    onRoleChange(grant, newRole);
  };

  const handleRemove = (grant) => {
    if (!isAdmin || grant.isOwner || !grant.canRemove) return;
    
    if (
      window.confirm(
        t('records.share.manage.confirm_remove_access', {
          name: grant.name,
          defaultValue: `Remove ${grant.name}'s access to this record?`
        })
      )
    ) {
      onRemove(grant);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        {t('records.share.loading_permissions', 'Loading permissions...')}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.table}>
        {grantDetails.map(grant => (
          <div key={grant.id} className={styles.row}>
            <div className={styles.principal}>
              {(grant.type === 'group' ? (
                <Avatar
                  variant={AVATAR_VARIANTS.GROUP}
                  name={grant.name}
                  src={grant.avatar}
                  shape="rounded"
                  alt={t('records.share.manage.group_avatar_alt', {
                    name: grant.name || t('records.share.manage.group_label', 'Group'),
                    defaultValue: `${grant.name || 'Group'} group avatar`
                  })}
                  textColor="var(--text-inverse)"
                  className={styles.principalAvatar}
                />
              ) : (
                <Avatar
                  name={grant.name}
                  src={grant.avatar}
                  alt={t('records.share.manage.user_avatar_alt', {
                    name: grant.name || t('records.share.manage.user_label', 'User'),
                    defaultValue: `${grant.name || 'User'} avatar`
                  })}
                  textColor="var(--text-inverse)"
                  className={styles.principalAvatar}
                />
              ))}
              <div className={styles.principalInfo}>
                <div className={styles.principalName}>
                  {grant.name}
                </div>
                <div className={styles.principalMeta}>
                  {grant.isOwner ? (
                    t('records.share.manage.owner', 'Owner')
                  ) : grant.type === 'user' ? (
                    t('records.share.manage.direct_access', 'Direct access')
                  ) : (
                    <>
                      {t('records.share.manage.group_member_count', {
                        count: grant.memberCount ?? 0,
                        defaultValue: `Group · ${grant.memberCount ?? 0} members`
                      })}
                    </>
                  )}
                  {grant.sources?.length > 0 && grant.sources[0] !== 'owner' && (
                    <span className={styles.viaBadge}>
                      {t('records.share.manage.via_source', {
                        source: grant.sources[0],
                        defaultValue: `via ${grant.sources[0]}`
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              {grant.isOwner ? (
                <div className={styles.ownerRole}>
                  <Lock size={14} />
                  <span>{t('records.share.manage.owner_admin', 'Owner (Admin)')}</span>
                </div>
              ) : isAdmin ? (
                <>
                  <RoleSelect
                    value={grant.role}
                    onChange={(newRole) => handleRoleChange(grant, newRole)}
                    disabled={!isAdmin}
                    showDescription={false}
                    className={styles.roleSelect}
                    iconMode="auto"
                  />
                  {grant.canRemove && (
                    <button
                      onClick={() => handleRemove(grant)}
                      className={styles.removeButton}
                      aria-label={t('records.share.manage.remove_access_aria', {
                        name: grant.name,
                        defaultValue: `Remove ${grant.name}'s access`
                      })}
                    >
                      <X size={18} />
                    </button>
                  )}
                </>
              ) : (
                <div className={styles.readOnlyRole}>
                  {t(`records.share.roles.${grant.role}.title`, grant.role)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {loadError && (
        <div className={styles.emptyMessage}>
          {t(loadError)}
        </div>
      )}

      {grantDetails.length === 1 && (
        <div className={styles.emptyMessage}>
          {t('records.share.manage.empty', 'This record is not shared with anyone yet.')}
        </div>
      )}
    </div>
  );
};

export default ManageAccessTable;
